export interface MenuItemTypes {
  key: string;
  label: string;
  isTitle?: boolean;
  icon?: string;
  url?: string;
  badge?: {
    variant: string;
    text: string;
  };
  parentKey?: string;
  target?: string;
  permissions?: string[];
  children?: MenuItemTypes[];
  perm_ids?: number[];
  // Optional: restrict visibility to suppliers that provide any of these service IDs
  requiredServiceIds?: number[];
}

// AI Request Dispatcher (P-4): "My Scorecard" is NOT part of MENU_ITEMS — it is
// admin-flag-gated (default OFF) and injected by helpers/menu.ts only after the
// /supplier-api/dispatch/my-scorecard probe returns 200 (see helpers/dispatchFlags.ts).
// When the flag is off the entry must not render at all (no teaser).
// An affiliate's optional upgrade to a service provider (2026-10-19): helpers/menu.ts
// adds it to the affiliate menu until the upgrade is approved.
export const BECOME_SERVICE_PROVIDER_MENU_ITEM: MenuItemTypes = {
  key: "become-service-provider",
  label: "Become a service provider",
  isTitle: false,
  icon: "briefcase",
  url: "/supplier/become-service-provider",
};

export const SCORECARD_MENU_ITEM: MenuItemTypes = {
  key: "my-scorecard",
  label: "My Scorecard",
  isTitle: false,
  icon: "bar-chart-2",
  url: "/supplier/my-scorecard",
};

// Affiliate referrers: not part of MENU_ITEMS — helpers/menu.ts injects them for
// companies that may refer (gate-status canActAsReferrer / isReferrerOnly).
export const REFERRAL_MENU_ITEMS: MenuItemTypes[] = [
  { key: "referral-title", label: "Referrals", isTitle: true },
  {
    key: "referral-links",
    label: "Links",
    isTitle: false,
    icon: "link",
    url: "/supplier/referral-links",
    permissions: ["supplier_manage_referral_links"],
  },
  {
    key: "referees",
    label: "Referees",
    isTitle: false,
    icon: "users",
    url: "/supplier/referees",
    permissions: ["supplier_see_referrals"],
  },
  // The same block as the Finance page's "Referral earnings" tab, on a page of
  // its own so a referrer finds its money under Referrals too (owner, 2026-10-15).
  {
    key: "referral-earnings",
    label: "Earnings",
    isTitle: false,
    icon: "bar-chart-2",
    url: "/supplier/referral-earnings",
    permissions: ["supplier_see_finance"],
  },
];

const MENU_ITEMS: MenuItemTypes[] = [
  { key: "navigation", label: "Home", isTitle: true },
  {
    key: "dashboard",
    label: "Dashboard",
    isTitle: false,
    icon: "dashboard-grid",
    url: "/dashboard",
  },
  { key: "navigation", label: "Commercials", isTitle: true },
  {
    key: "requests",
    label: "Requests",
    isTitle: false,
    icon: "requests-globe",
    permissions: ["supplier_available_list_request_service"],
    url: "/supplier/available-requests",
  },
  {
    key: "offers",
    label: "Offers",
    isTitle: false,
    icon: "offers-badge",
    permissions: ["supplier_get_offers"],
    url: "/supplier/offers",
  },
  {
    // Exclusive referrals: the customers this supplier brought, whose requests
    // (for the services it offers) only it sees. Every supplier is a partner by default.
    key: "referred-customers",
    label: "Referred customers",
    isTitle: false,
    icon: "users",
    permissions: ["supplier_see_exclusive_customers"],
    url: "/supplier/referred-customers",
  },

  { key: "inventory", label: "My Fleet & Inventory", isTitle: true },
  {
    key: "hotels",
    label: "Hotels",
    isTitle: false,
    icon: "hotels",
    permissions: ["supplier_see_hotels"],
    perm_ids: [3, 5],
    requiredServiceIds: [3, 5],
    url: "/supplier/hotels",
  },
  {
    key: "restaurants",
    label: "Restaurants",
    isTitle: false,
    icon: "restaurant",
    permissions: ["supplier_see_hotels"],
    perm_ids: [4],
    requiredServiceIds: [4],
    url: "/supplier/restaurants",
  },
  {
    key: "events",
    label: "Events",
    isTitle: false,
    icon: "events",
    permissions: ["supplier_see_events"],
    perm_ids: [7],
    requiredServiceIds: [7],
    url: "/supplier/events",
  },
  {
    key: "aircraft-list",
    label: "Aircraft List",
    isTitle: false,
    icon: "aircrafts",
    url: "/supplier/aircrafts",
    permissions: ["supplier_aircraft_read"],
    requiredServiceIds: [1],
  },
  {
    key: "empty-legs",
    label: "Empty Legs",
    isTitle: false,
    icon: "empty-leg",
    url: "/supplier/empty-legs",
    permissions: ["supplier_emptyleg_read"],
    requiredServiceIds: [1],
  },
  {
    key: "yachts",
    label: "Yachts",
    url: "/supplier/yachts",
    icon: "yachts",
    permissions: ["supplier_yachts_read"],
    requiredServiceIds: [6],
  },
  {
    key: "experiences",
    label: "Experiences",
    url: "/supplier/experiences",
    icon: "compass",
    permissions: ["supplier_manage_experiences"],
    requiredServiceIds: [12],
  },
  {
    key: "cars",
    label: "Cars",
    url: "/supplier/cars",
    icon: "cars",
    permissions: ["supplier_cars_read"],
    requiredServiceIds: [9, 10],
  },
  {
    key: "products",
    label: "Products",
    isTitle: false,
    icon: "shopping-bag",
    permissions: ["supplier_products_read"],
    requiredServiceIds: [11],
    url: "/supplier/products",
  },
  // Supplier promotions (2026-10-27): recommended cards now, search boost next.
  {
    key: "promotions",
    label: "Promotions",
    isTitle: false,
    icon: "star",
    permissions: ["supplier_manage_promotions"],
    url: "/supplier/promotions",
  },
  {
    key: "services",
    label: "Services",
    isTitle: false,
    icon: "services",
    permissions: ["supplier_see_services"],
    url: "/supplier/services",
  },
  { key: "documents", label: "Documents", isTitle: true },
  {
    key: "compliance_documents",
    label: "Compliance Documents",
    isTitle: false,
    icon: "support-headset",
    url: "/supplier/onboarding",
  },
  {
    key: "inventory_compliance",
    label: "Inventory Compliance",
    isTitle: false,
    icon: "shield",
    url: "/supplier/inventory-compliance",
  },
  // Moved out of onboarding on 2026-10-02: per-service compliance evidence.
  {
    key: "service_documents",
    label: "Service Documents",
    isTitle: false,
    icon: "file-text",
    url: "/supplier/service-documents",
  },
  {
    key: "contracts",
    label: "Contracts",
    isTitle: false,
    icon: "clipboard",
    url: "/supplier/contracts",
  },
    {
    key: "invoices",
    label: "Invoices",
    isTitle: false,
    icon: "invoices-doc",
    permissions: ["supplier_view_supplier_invoices"],
    url: "/supplier/invoices",
  },
  {
    key: "finance",
    label: "Finance",
    isTitle: false,
    icon: "credit-card",
    url: "/supplier/finance",
    permissions: ["supplier_see_finance"],
  },
  // Moved out of onboarding on 2026-10-02: settlement accounts are managed here.
  {
    key: "bank-details",
    label: "Bank Details",
    isTitle: false,
    icon: "briefcase",
    url: "/supplier/bank",
  },
  { key: "help", label: "Help", isTitle: true },
  {
    key: "support",
    label: "Support",
    isTitle: false,
    icon: "support-headset",
    url: "/supplier/support/chat",
  },
  {
    key: "ticket",
    label: "Ticket",
    isTitle: false,
    icon: "ticket-bubbles",
    url: "/supplier/tickets",
  },
  {
    key: "documentation",
    label: "Supplier Guide",
    isTitle: false,
    icon: "documentation-folder",
    url: "/supplier/documentation",
  },
  {
    key: "team",
    label: "Team",
    isTitle: false,
    icon: "settings-sliders",
    url: "/supplier/team",
    permissions: ["supplier_see_users"],
  },
  // Self-service Listings-API keys (2026-10-21).
  {
    key: "api-keys",
    label: "API keys",
    isTitle: false,
    icon: "key",
    url: "/supplier/api-keys",
    permissions: ["supplier_manage_api_keys"],
  },
  {
    key: "settings",
    label: "Settings",
    isTitle: false,
    icon: "settings-sliders",
    url: "/profile",
  },
  // Onboarding — a single entry at the bottom of the menu (under Help). The KYB
  // steps (application form, compliance questionnaire, compliance documents,
  // service documents, bank details) live inside the onboarding page's own nav
  // rail (see components/onboarding/OnboardingSubnav).
  {
    key: "onboarding",
    label: "Onboarding",
    isTitle: false,
    icon: "dashboard-grid",
    url: "/supplier/onboarding",
  },
];

const HORIZONTAL_MENU_ITEMS: MenuItemTypes[] = [
  {
    key: "dashboard",
    icon: "home",
    label: "Dashboard",
    isTitle: true,
    children: [
      {
        key: "ds-dashboard-1",
        label: "Dashboard 1",
        url: "/dashboard-1",
        parentKey: "dashboard",
      },
      {
        key: "ds-dashboard-2",
        label: "Dashboard 2",
        url: "/dashboard-2",
        parentKey: "dashboard",
      },
      {
        key: "ds-dashboard-3",
        label: "Dashboard 3",
        url: "/dashboard-3",
        parentKey: "dashboard",
      },
      {
        key: "ds-dashboard-4",
        label: "Dashboard 4",
        url: "/dashboard-4",
        parentKey: "dashboard",
      },
    ],
  },
  {
    key: "apps",
    icon: "grid",
    label: "Apps",
    isTitle: true,
    children: [
      {
        key: "apps-calendar",
        label: "Calendar",
        isTitle: false,
        icon: "calendar",
        url: "/apps/calendar",
        parentKey: "apps",
      },
      {
        key: "apps-chat",
        label: "Chat",
        isTitle: false,
        icon: "message-square",
        url: "/apps/chat",
        parentKey: "apps",
      },
      {
        key: "apps-ecommerce",
        label: "Ecommerce",
        isTitle: false,
        icon: "shopping-cart",
        parentKey: "apps",
        children: [
          {
            key: "ecommerce-dashboard",
            label: "Dashboard",
            url: "/apps/ecommerce/dashboard",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-products",
            label: "Products",
            url: "/apps/ecommerce/products",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-details",
            label: "Product Details",
            url: "/apps/ecommerce/product-details",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-edit-product",
            label: "Add Product",
            url: "/apps/ecommerce/edit-product",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-customers",
            label: "Customers",
            url: "/apps/ecommerce/customers",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-orders",
            label: "Orders",
            url: "/apps/ecommerce/orders",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-order-details",
            label: "Order Details",
            url: "/apps/ecommerce/order/details",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-sellers",
            label: "Sellers",
            url: "/apps/ecommerce/sellers",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-shopping-cart",
            label: "Shopping Cart",
            url: "/apps/ecommerce/shopping-cart",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-checkout",
            label: "Checkout",
            url: "/apps/ecommerce/checkout",
            parentKey: "apps-ecommerce",
          },
        ],
      },
      {
        key: "apps-crm",
        label: "CRM",
        isTitle: false,
        icon: "users",
        parentKey: "apps",
        children: [
          {
            key: "crm-dashboard",
            label: "Dashboard",
            url: "/apps/crm/dashboard",
            parentKey: "apps-crm",
          },
          {
            key: "crm-contacts",
            label: "Contacts",
            url: "/apps/crm/contacts",
            parentKey: "apps-crm",
          },
          {
            key: "crm-opportunities",
            label: "Opportunities",
            url: "/apps/crm/opportunities",
            parentKey: "apps-crm",
          },
          {
            key: "crm-leads",
            label: "Leads",
            url: "/apps/crm/leads",
            parentKey: "apps-crm",
          },
          {
            key: "crm-customers",
            label: "Customers",
            url: "/apps/crm/customers",
            parentKey: "apps-crm",
          },
        ],
      },
      {
        key: "apps-email",
        label: "Email",
        isTitle: false,
        icon: "mail",
        parentKey: "apps",
        children: [
          {
            key: "email-inbox",
            label: "Inbox",
            url: "/apps/email/inbox",
            parentKey: "apps-email",
          },
          {
            key: "email-read-email",
            label: "Read Email",
            url: "/apps/email/details",
            parentKey: "apps-email",
          },
          {
            key: "email-compose-email",
            label: "Compose Email",
            url: "/apps/email/compose",
            parentKey: "apps-email",
          },
        ],
      },
      {
        key: "apps-social",
        label: "Social Feed",
        isTitle: false,
        icon: "rss",
        url: "/apps/social-feed",
        badge: { variant: "pink", text: "Hot" },
        parentKey: "apps",
      },
      {
        key: "apps-companies",
        label: "Companies",
        isTitle: false,
        icon: "activity",
        url: "/apps/companies",
        parentKey: "apps",
      },
      {
        key: "apps-projects",
        label: "Projects",
        isTitle: false,
        icon: "briefcase",
        parentKey: "apps",
        children: [
          {
            key: "project-list",
            label: "List",
            url: "/apps/projects/list",
            parentKey: "apps-projects",
          },
          {
            key: "project-details",
            label: "Details",
            url: "/apps/projects/:id/details",
            parentKey: "apps-projects",
          },
          {
            key: "project-create-project",
            label: "Create Project",
            url: "/apps/projects/create",
            parentKey: "apps-projects",
          },
        ],
      },
      {
        key: "apps-tasks",
        label: "Tasks",
        isTitle: false,
        icon: "clipboard",
        parentKey: "apps",
        children: [
          {
            key: "task-list",
            label: "List",
            url: "/apps/tasks/list",
            parentKey: "apps-tasks",
          },
          {
            key: "task-details",
            label: "Details",
            url: "/apps/tasks/details",
            parentKey: "apps-tasks",
          },
          {
            key: "task-kanban",
            label: "Kanban Board",
            url: "/apps/tasks/kanban",
            parentKey: "apps-tasks",
          },
        ],
      },
      {
        key: "apps-contacts",
        label: "Contacts",
        isTitle: false,
        icon: "book",
        parentKey: "apps",
        children: [
          {
            key: "contacts-list",
            label: "Members List",
            url: "/apps/contacts/list",
            parentKey: "apps-contacts",
          },
          {
            key: "contacts-profile",
            label: "Profile",
            url: "/apps/contacts/profile",
            parentKey: "apps-contacts",
          },
        ],
      },
      {
        key: "apps-tickets",
        label: "Tickets",
        isTitle: false,
        icon: "aperture",
        parentKey: "apps",
        children: [
          {
            key: "tickets-list",
            label: "List",
            url: "/apps/tickets/list",
            parentKey: "apps-tickets",
          },
          {
            key: "tickets-details",
            label: "Details",
            url: "/apps/tickets/details",
            parentKey: "apps-tickets",
          },
        ],
      },
      {
        key: "apps-file-manager",
        label: "File Manager",
        isTitle: false,
        icon: "folder-plus",
        url: "/apps/file-manager",
        parentKey: "apps",
      },
    ],
  },
  {
    key: "base-ui",
    icon: "briefcase",
    label: "UI Elements",
    isTitle: true,
    children: [
      {
        key: "base-ui-buttons",
        label: "Buttons",
        url: "/ui/buttons",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-cards",
        label: "Cards",
        url: "/ui/cards",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-avatars",
        label: "Avatars",
        url: "/ui/avatars",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-portlets",
        label: "Portlets",
        url: "/ui/portlets",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-tabs-accordions",
        label: "Tabs & Accordions",
        url: "/ui/tabs-accordions",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-modals",
        label: "Modals",
        url: "/ui/modals",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-progress",
        label: "Progress",
        url: "/ui/progress",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-notifications",
        label: "Notifications",
        url: "/ui/notifications",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-offcanvas",
        label: "Offcanvas",
        url: "/ui/offcanvas",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-placeholders",
        label: "Placeholders",
        url: "/ui/placeholders",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-spinners",
        label: "Spinners",
        url: "/ui/spinners",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-images",
        label: "Images",
        url: "/ui/images",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-carousel",
        label: "Carousel",
        url: "/ui/carousel",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-listgroups",
        label: "List Groups",
        url: "/ui/listgroups",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-embedvideo",
        label: "Embed Video",
        url: "/ui/embedvideo",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-dropdown",
        label: "Dropdowns",
        url: "/ui/dropdowns",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-ribbons",
        label: "Ribbons",
        url: "/ui/ribbons",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-tooltips-popovers",
        label: "Tooltips & Popovers",
        url: "/ui/tooltips-popovers",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-general",
        label: "General UI",
        url: "/ui/general",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-typography",
        label: "Typography",
        url: "/ui/typography",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-grid",
        label: "Grid",
        url: "/ui/grid",
        parentKey: "base-ui",
      },
    ],
  },
  {
    key: "components",
    icon: "package",
    label: "Components",
    isTitle: true,
    children: [
      {
        key: "extended-ui",
        label: "Extended UI",
        isTitle: false,
        icon: "layers",
        badge: { variant: "info", text: "Hot" },
        parentKey: "components",
        children: [
          {
            key: "extended-ui-nestable",
            label: "Nestable List",
            url: "/extended-ui/nestable",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-dragdrop",
            label: "Drag and Drop",
            url: "/extended-ui/dragdrop",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-rangesliders",
            label: "Range Sliders",
            url: "/extended-ui/rangesliders",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-animation",
            label: "Animation",
            url: "/extended-ui/animation",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-sweet-alert",
            label: "Sweet Alert",
            url: "/extended-ui/sweet-alert",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-tour",
            label: "Tour Page",
            url: "/extended-ui/tour",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-loading-buttons",
            label: "Loading Buttons",
            url: "/extended-ui/loading-buttons",
            parentKey: "extended-ui",
          },
        ],
      },
      {
        key: "widgets",
        label: "Widgets",
        isTitle: false,
        icon: "gift",
        url: "/ui/widgets",
        parentKey: "components",
      },
      {
        key: "icons",
        label: "Icons",
        isTitle: false,
        icon: "cpu",
        parentKey: "components",
        children: [
          {
            key: "icon-two-tone",
            label: "Two Tone Icons",
            url: "/ui/icons/two-tone",
            parentKey: "icons",
          },
          {
            key: "icon-feather",
            label: "Feather Icons",
            url: "/ui/icons/feather",
            parentKey: "icons",
          },
          {
            key: "icon-mdiicons",
            label: "Material Design Icons",
            url: "/ui/icons/mdi",
            parentKey: "icons",
          },
          {
            key: "icon-dripicons",
            label: "Dripicons",
            url: "/ui/icons/dripicons",
            parentKey: "icons",
          },
          {
            key: "icon-font-awesome",
            label: "Font Awesome 5",
            url: "/ui/icons/font-awesome",
            parentKey: "icons",
          },
          {
            key: "icon-themify",
            label: "Themify",
            url: "/ui/icons/themify",
            parentKey: "icons",
          },
          {
            key: "icon-simple-line",
            label: "Simple Line",
            url: "/ui/icons/simple-line",
            parentKey: "icons",
          },
          {
            key: "icon-weather",
            label: "Weather",
            url: "/ui/icons/weather",
            parentKey: "icons",
          },
        ],
      },
      {
        key: "forms",
        label: "Forms",
        isTitle: false,
        icon: "bookmark",
        parentKey: "components",
        children: [
          {
            key: "form-basic",
            label: "General Elements",
            url: "/ui/forms/basic",
            parentKey: "forms",
          },
          {
            key: "form-advanced",
            label: "Form Advanced",
            url: "/ui/forms/advanced",
            parentKey: "forms",
          },
          {
            key: "form-validation",
            label: "Validation",
            url: "/ui/forms/validation",
            parentKey: "forms",
          },
          {
            key: "form-wizard",
            label: "Wizard",
            url: "/ui/forms/wizard",
            parentKey: "forms",
          },
          {
            key: "form-upload",
            label: "File Uploads",
            url: "/ui/forms/upload",
            parentKey: "forms",
          },
          {
            key: "form-editors",
            label: "Editors",
            url: "/ui/forms/editors",
            parentKey: "forms",
          },
        ],
      },
      {
        key: "tables",
        label: "Tables",
        isTitle: false,
        icon: "grid",
        parentKey: "components",
        children: [
          {
            key: "table-basic",
            label: "Basic Tables",
            url: "/ui/tables/basic",
            parentKey: "tables",
          },
          {
            key: "table-advanced",
            label: "Advanced Tables",
            url: "/ui/tables/advanced",
            parentKey: "tables",
          },
        ],
      },
      {
        key: "charts",
        label: "Charts",
        isTitle: false,
        parentKey: "components",
        icon: "bar-chart-2",
        children: [
          {
            key: "chart-apex",
            label: "Apex Charts",
            url: "/ui/charts/apex",
            parentKey: "charts",
          },
          {
            key: "chart-chartjs",
            label: "Chartjs",
            url: "/ui/charts/chartjs",
            parentKey: "charts",
          },
        ],
      },
      {
        key: "maps",
        label: "Maps",
        isTitle: false,
        icon: "map",
        parentKey: "components",
        children: [
          {
            key: "maps-googlemaps",
            label: "Google Maps",
            url: "/ui/googlemaps",
            parentKey: "maps",
          },
          {
            key: "maps-vectormaps",
            label: "Vector Maps",
            url: "/ui/vectormaps",
            parentKey: "maps",
          },
        ],
      },
      {
        key: "menu-levels",
        label: "Menu Levels",
        isTitle: false,
        icon: "share-2",
        parentKey: "components",
        children: [
          {
            key: "menu-levels-1-1",
            label: "Level 1.1",
            url: "/",
            parentKey: "menu-levels",
            children: [
              {
                key: "menu-levels-2-1",
                label: "Level 2.1",
                url: "/",
                parentKey: "menu-levels-1-1",
                children: [
                  {
                    key: "menu-levels-3-1",
                    label: "Level 3.1",
                    url: "/",
                    parentKey: "menu-levels-2-1",
                  },
                  {
                    key: "menu-levels-3-2",
                    label: "Level 3.2",
                    url: "/",
                    parentKey: "menu-levels-2-1",
                  },
                ],
              },
              {
                key: "menu-levels-2-2",
                label: "Level 2.2",
                url: "/",
                parentKey: "menu-levels-1-1",
              },
            ],
          },
          {
            key: "menu-levels-1-2",
            label: "Level 1.2",
            url: "/",
            parentKey: "menu-levels",
          },
        ],
      },
    ],
  },
  {
    key: "pages",
    icon: "file-text",
    label: "Pages",
    isTitle: true,
    children: [
      {
        key: "error-pages",
        label: "Errors",
        isTitle: false,
        parentKey: "pages",
        children: [
          {
            key: "page-error-404",
            label: "Error - 404",
            url: "/error-404",
            parentKey: "error-pages",
          },
          {
            key: "page-error-404-two",
            label: "Error - 404 Two",
            url: "/error-404-two",
            parentKey: "error-pages",
          },
          {
            key: "page-error-404-alt",
            label: "Error - 404-alt",
            url: "/pages/error-404-alt",
            parentKey: "error-pages",
          },
          {
            key: "page-error-500",
            label: "Error - 500",
            url: "/error-500",
            parentKey: "error-pages",
          },
          {
            key: "page-error-500-two",
            label: "Error - 500 Two",
            url: "/error-500-two",
            parentKey: "error-pages",
          },
        ],
      },
      {
        key: "extra-pages",
        label: "Utility",
        isTitle: false,
        parentKey: "pages",
        children: [
          {
            key: "page-starter",
            label: "Starter",
            url: "/pages/starter",
            parentKey: "extra-pages",
          },
          {
            key: "page-timeline",
            label: "Timeline",
            url: "/pages/timeline",
            parentKey: "extra-pages",
          },
          {
            key: "page-sitemap",
            label: "Sitemap",
            url: "/pages/sitemap",
            parentKey: "extra-pages",
          },
          {
            key: "page-invoice",
            label: "Invoice",
            url: "/pages/invoice",
            parentKey: "extra-pages",
          },
          {
            key: "page-faq",
            label: "FAQs",
            url: "/pages/faq",
            parentKey: "extra-pages",
          },
          {
            key: "page-search-result",
            label: "Search Results",
            url: "/pages/serach-results",
            parentKey: "extra-pages",
          },
          {
            key: "page-pricing",
            label: "Pricing",
            url: "/pages/pricing",
            parentKey: "extra-pages",
          },
          {
            key: "page-maintenance",
            label: "Maintenance",
            url: "/maintenance",
            target: "_blank",
            parentKey: "extra-pages",
          },
          {
            key: "page-upcoming",
            label: "Coming Soon",
            url: "/upcoming",
            parentKey: "extra-pages",
          },
          {
            key: "page-gallery",
            label: "Gallery",
            url: "/pages/gallery",
            parentKey: "extra-pages",
          },
        ],
      },
    ],
  },
];

const TWO_COl_MENU_ITEMS: MenuItemTypes[] = [
  {
    key: "dashboard",
    icon: "home",
    label: "Dashboard",
    isTitle: true,
    children: [
      {
        key: "ds-dashboard-1",
        label: "Dashboard 1",
        url: "/dashboard-1",
        parentKey: "dashboard",
      },
      {
        key: "ds-dashboard-2",
        label: "Dashboard 2",
        url: "/dashboard-2",
        parentKey: "dashboard",
      },
      {
        key: "ds-dashboard-3",
        label: "Dashboard 3",
        url: "/dashboard-3",
        parentKey: "dashboard",
      },
      {
        key: "ds-dashboard-4",
        label: "Dashboard 4",
        url: "/dashboard-4",
        parentKey: "dashboard",
      },
    ],
  },
  {
    key: "apps",
    icon: "grid",
    label: "Apps",
    isTitle: true,
    children: [
      {
        key: "apps-calendar",
        label: "Calendar",
        isTitle: false,
        icon: "calendar",
        url: "/apps/calendar",
        parentKey: "apps",
      },
      {
        key: "apps-chat",
        label: "Chat",
        isTitle: false,
        icon: "message-square",
        url: "/apps/chat",
        parentKey: "apps",
      },
      {
        key: "apps-ecommerce",
        label: "Ecommerce",
        isTitle: false,
        icon: "shopping-cart",
        parentKey: "apps",
        children: [
          {
            key: "ecommerce-dashboard",
            label: "Dashboard",
            url: "/apps/ecommerce/dashboard",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-products",
            label: "Products",
            url: "/apps/ecommerce/products",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-details",
            label: "Product Details",
            url: "/apps/ecommerce/product-details",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-edit-product",
            label: "Add Product",
            url: "/apps/ecommerce/edit-product",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-customers",
            label: "Customers",
            url: "/apps/ecommerce/customers",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-orders",
            label: "Orders",
            url: "/apps/ecommerce/orders",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-order-details",
            label: "Order Details",
            url: "/apps/ecommerce/order/details",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-sellers",
            label: "Sellers",
            url: "/apps/ecommerce/sellers",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-shopping-cart",
            label: "Shopping Cart",
            url: "/apps/ecommerce/shopping-cart",
            parentKey: "apps-ecommerce",
          },
          {
            key: "ecommerce-checkout",
            label: "Checkout",
            url: "/apps/ecommerce/checkout",
            parentKey: "apps-ecommerce",
          },
        ],
      },
      {
        key: "apps-crm",
        label: "CRM",
        isTitle: false,
        icon: "users",
        parentKey: "apps",
        children: [
          {
            key: "crm-dashboard",
            label: "Dashboard",
            url: "/apps/crm/dashboard",
            parentKey: "apps-crm",
          },
          {
            key: "crm-contacts",
            label: "Contacts",
            url: "/apps/crm/contacts",
            parentKey: "apps-crm",
          },
          {
            key: "crm-opportunities",
            label: "Opportunities",
            url: "/apps/crm/opportunities",
            parentKey: "apps-crm",
          },
          {
            key: "crm-leads",
            label: "Leads",
            url: "/apps/crm/leads",
            parentKey: "apps-crm",
          },
          {
            key: "crm-customers",
            label: "Customers",
            url: "/apps/crm/customers",
            parentKey: "apps-crm",
          },
        ],
      },
      {
        key: "apps-email",
        label: "Email",
        isTitle: false,
        icon: "mail",
        parentKey: "apps",
        children: [
          {
            key: "email-inbox",
            label: "Inbox",
            url: "/apps/email/inbox",
            parentKey: "apps-email",
          },
          {
            key: "email-read-email",
            label: "Read Email",
            url: "/apps/email/details",
            parentKey: "apps-email",
          },
          {
            key: "email-compose-email",
            label: "Compose Email",
            url: "/apps/email/compose",
            parentKey: "apps-email",
          },
        ],
      },
      {
        key: "apps-social",
        label: "Social Feed",
        isTitle: false,
        icon: "rss",
        url: "/apps/social-feed",
        badge: { variant: "pink", text: "Hot" },
        parentKey: "apps",
      },
      {
        key: "apps-companies",
        label: "Companies",
        isTitle: false,
        icon: "activity",
        url: "/apps/companies",
        parentKey: "apps",
      },
      {
        key: "apps-projects",
        label: "Projects",
        isTitle: false,
        icon: "briefcase",
        parentKey: "apps",
        children: [
          {
            key: "project-list",
            label: "List",
            url: "/apps/projects/list",
            parentKey: "apps-projects",
          },
          {
            key: "project-details",
            label: "Details",
            url: "/apps/projects/:id/details",
            parentKey: "apps-projects",
          },
          {
            key: "project-create-project",
            label: "Create Project",
            url: "/apps/projects/create",
            parentKey: "apps-projects",
          },
        ],
      },
      {
        key: "apps-tasks",
        label: "Tasks",
        isTitle: false,
        icon: "clipboard",
        parentKey: "apps",
        children: [
          {
            key: "task-list",
            label: "List",
            url: "/apps/tasks/list",
            parentKey: "apps-tasks",
          },
          {
            key: "task-details",
            label: "Details",
            url: "/apps/tasks/details",
            parentKey: "apps-tasks",
          },
          {
            key: "task-kanban",
            label: "Kanban Board",
            url: "/apps/tasks/kanban",
            parentKey: "apps-tasks",
          },
        ],
      },
      {
        key: "apps-contacts",
        label: "Contacts",
        isTitle: false,
        icon: "book",
        parentKey: "apps",
        children: [
          {
            key: "contacts-list",
            label: "Members List",
            url: "/apps/contacts/list",
            parentKey: "apps-contacts",
          },
          {
            key: "contacts-profile",
            label: "Profile",
            url: "/apps/contacts/profile",
            parentKey: "apps-contacts",
          },
        ],
      },
      {
        key: "apps-tickets",
        label: "Tickets",
        isTitle: false,
        icon: "aperture",
        parentKey: "apps",
        children: [
          {
            key: "tickets-list",
            label: "List",
            url: "/apps/tickets/list",
            parentKey: "apps-tickets",
          },
          {
            key: "tickets-details",
            label: "Details",
            url: "/apps/tickets/details",
            parentKey: "apps-tickets",
          },
        ],
      },
      {
        key: "apps-file-manager",
        label: "File Manager",
        isTitle: false,
        icon: "folder-plus",
        url: "/apps/file-manager",
        parentKey: "apps",
      },
    ],
  },
  {
    key: "extra-pages",
    icon: "file-text",
    label: "Pages",
    isTitle: true,
    children: [
      {
        key: "page-starter",
        label: "Starter",
        url: "/pages/starter",
        parentKey: "extra-pages",
      },
      {
        key: "page-timeline",
        label: "Timeline",
        url: "/pages/timeline",
        parentKey: "extra-pages",
      },
      {
        key: "page-sitemap",
        label: "Sitemap",
        url: "/pages/sitemap",
        parentKey: "extra-pages",
      },
      {
        key: "page-invoice",
        label: "Invoice",
        url: "/pages/invoice",
        parentKey: "extra-pages",
      },
      {
        key: "page-faq",
        label: "FAQs",
        url: "/pages/faq",
        parentKey: "extra-pages",
      },
      {
        key: "page-search-result",
        label: "Search Results",
        url: "/pages/serach-results",
        parentKey: "extra-pages",
      },
      {
        key: "page-pricing",
        label: "Pricing",
        url: "/pages/pricing",
        parentKey: "extra-pages",
      },
      {
        key: "page-maintenance",
        label: "Maintenance",
        url: "/maintenance",
        target: "_blank",
        parentKey: "extra-pages",
      },
      {
        key: "page-upcoming",
        label: "Coming Soon",
        url: "/upcoming",
        parentKey: "extra-pages",
      },
      {
        key: "page-gallery",
        label: "Gallery",
        url: "/pages/gallery",
        parentKey: "extra-pages",
      },

      {
        key: "page-error-404",
        label: "Error - 404",
        url: "/error-404",
        parentKey: "extra-pages",
      },
      {
        key: "page-error-404-two",
        label: "Error - 404 Two",
        url: "/error-404-two",
        parentKey: "extra-pages",
      },
      {
        key: "page-error-404-alt",
        label: "Error - 404-alt",
        url: "/pages/error-404-alt",
        parentKey: "extra-pages",
      },
      {
        key: "page-error-500",
        label: "Error - 500",
        url: "/error-500",
        parentKey: "extra-pages",
      },
      {
        key: "page-error-500-two",
        label: "Error - 500 Two",
        url: "/error-500-two",
        parentKey: "extra-pages",
      },
    ],
  },
  {
    key: "base-ui",
    icon: "briefcase",
    label: "UI Elements",
    isTitle: true,
    children: [
      {
        key: "base-ui-buttons",
        label: "Buttons",
        url: "/ui/buttons",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-cards",
        label: "Cards",
        url: "/ui/cards",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-avatars",
        label: "Avatars",
        url: "/ui/avatars",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-portlets",
        label: "Portlets",
        url: "/ui/portlets",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-tabs-accordions",
        label: "Tabs & Accordions",
        url: "/ui/tabs-accordions",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-modals",
        label: "Modals",
        url: "/ui/modals",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-progress",
        label: "Progress",
        url: "/ui/progress",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-notifications",
        label: "Notifications",
        url: "/ui/notifications",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-offcanvas",
        label: "Offcanvas",
        url: "/ui/offcanvas",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-placeholders",
        label: "Placeholders",
        url: "/ui/placeholders",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-spinners",
        label: "Spinners",
        url: "/ui/spinners",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-images",
        label: "Images",
        url: "/ui/images",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-carousel",
        label: "Carousel",
        url: "/ui/carousel",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-listgroups",
        label: "List Groups",
        url: "/ui/listgroups",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-embedvideo",
        label: "Embed Video",
        url: "/ui/embedvideo",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-dropdown",
        label: "Dropdowns",
        url: "/ui/dropdowns",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-ribbons",
        label: "Ribbons",
        url: "/ui/ribbons",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-tooltips-popovers",
        label: "Tooltips & Popovers",
        url: "/ui/tooltips-popovers",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-general",
        label: "General UI",
        url: "/ui/general",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-typography",
        label: "Typography",
        url: "/ui/typography",
        parentKey: "base-ui",
      },
      {
        key: "base-ui-grid",
        label: "Grid",
        url: "/ui/grid",
        parentKey: "base-ui",
      },
    ],
  },
  {
    key: "components",
    icon: "package",
    label: "Components",
    isTitle: true,
    children: [
      {
        key: "extended-ui",
        label: "Extended UI",
        isTitle: false,
        icon: "layers",
        badge: { variant: "info", text: "Hot" },
        parentKey: "components",
        children: [
          {
            key: "extended-ui-nestable",
            label: "Nestable List",
            url: "/extended-ui/nestable",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-dragdrop",
            label: "Drag and Drop",
            url: "/extended-ui/dragdrop",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-rangesliders",
            label: "Range Sliders",
            url: "/extended-ui/rangesliders",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-animation",
            label: "Animation",
            url: "/extended-ui/animation",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-sweet-alert",
            label: "Sweet Alert",
            url: "/extended-ui/sweet-alert",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-tour",
            label: "Tour Page",
            url: "/extended-ui/tour",
            parentKey: "extended-ui",
          },
          {
            key: "extended-ui-loading-buttons",
            label: "Loading Buttons",
            url: "/extended-ui/loading-buttons",
            parentKey: "extended-ui",
          },
        ],
      },
      {
        key: "icons",
        label: "Icons",
        isTitle: false,
        icon: "cpu",
        parentKey: "components",
        children: [
          {
            key: "icon-two-tone",
            label: "Two Tone Icons",
            url: "/ui/icons/two-tone",
            parentKey: "icons",
          },
          {
            key: "icon-feather",
            label: "Feather Icons",
            url: "/ui/icons/feather",
            parentKey: "icons",
          },
          {
            key: "icon-mdiicons",
            label: "Material Design Icons",
            url: "/ui/icons/mdi",
            parentKey: "icons",
          },
          {
            key: "icon-dripicons",
            label: "Dripicons",
            url: "/ui/icons/dripicons",
            parentKey: "icons",
          },
          {
            key: "icon-font-awesome",
            label: "Font Awesome 5",
            url: "/ui/icons/font-awesome",
            parentKey: "icons",
          },
          {
            key: "icon-themify",
            label: "Themify",
            url: "/ui/icons/themify",
            parentKey: "icons",
          },
          {
            key: "icon-simple-line",
            label: "Simple Line",
            url: "/ui/icons/simple-line",
            parentKey: "icons",
          },
          {
            key: "icon-weather",
            label: "Weather",
            url: "/ui/icons/weather",
            parentKey: "icons",
          },
        ],
      },
      {
        key: "forms",
        label: "Forms",
        isTitle: false,
        icon: "bookmark",
        parentKey: "components",
        children: [
          {
            key: "form-basic",
            label: "General Elements",
            url: "/ui/forms/basic",
            parentKey: "forms",
          },
          {
            key: "form-advanced",
            label: "Form Advanced",
            url: "/ui/forms/advanced",
            parentKey: "forms",
          },
          {
            key: "form-validation",
            label: "Validation",
            url: "/ui/forms/validation",
            parentKey: "forms",
          },
          {
            key: "form-wizard",
            label: "Wizard",
            url: "/ui/forms/wizard",
            parentKey: "forms",
          },
          {
            key: "form-upload",
            label: "File Uploads",
            url: "/ui/forms/upload",
            parentKey: "forms",
          },
          {
            key: "form-editors",
            label: "Editors",
            url: "/ui/forms/editors",
            parentKey: "forms",
          },
        ],
      },
      {
        key: "tables",
        label: "Tables",
        isTitle: false,
        icon: "grid",
        parentKey: "components",
        children: [
          {
            key: "table-basic",
            label: "Basic Tables",
            url: "/ui/tables/basic",
            parentKey: "tables",
          },
          {
            key: "table-advanced",
            label: "Advanced Tables",
            url: "/ui/tables/advanced",
            parentKey: "tables",
          },
        ],
      },
      {
        key: "charts",
        label: "Charts",
        isTitle: false,
        parentKey: "components",
        icon: "bar-chart-2",
        children: [
          {
            key: "chart-apex",
            label: "Apex Charts",
            url: "/ui/charts/apex",
            parentKey: "charts",
          },
          {
            key: "chart-chartjs",
            label: "Chartjs",
            url: "/ui/charts/chartjs",
            parentKey: "charts",
          },
        ],
      },
      {
        key: "maps",
        label: "Maps",
        isTitle: false,
        icon: "map",
        parentKey: "components",
        children: [
          {
            key: "maps-googlemaps",
            label: "Google Maps",
            url: "/ui/googlemaps",
            parentKey: "maps",
          },
          {
            key: "maps-vectormaps",
            label: "Vector Maps",
            url: "/ui/vectormaps",
            parentKey: "maps",
          },
        ],
      },
      {
        key: "menu-levels",
        label: "Menu Levels",
        isTitle: false,
        icon: "share-2",
        parentKey: "components",
        children: [
          {
            key: "menu-levels-1-1",
            label: "Level 1.1",
            url: "/",
            parentKey: "menu-levels",
            children: [
              {
                key: "menu-levels-2-1",
                label: "Level 2.1",
                url: "/",
                parentKey: "menu-levels-1-1",
                children: [
                  {
                    key: "menu-levels-3-1",
                    label: "Level 3.1",
                    url: "/",
                    parentKey: "menu-levels-2-1",
                  },
                  {
                    key: "menu-levels-3-2",
                    label: "Level 3.2",
                    url: "/",
                    parentKey: "menu-levels-2-1",
                  },
                ],
              },
              {
                key: "menu-levels-2-2",
                label: "Level 2.2",
                url: "/",
                parentKey: "menu-levels-1-1",
              },
            ],
          },
          {
            key: "menu-levels-1-2",
            label: "Level 1.2",
            url: "/",
            parentKey: "menu-levels",
          },
        ],
      },
    ],
  },
  {
    isTitle: true,
    key: "widgets",
    label: "Other page",
    icon: "gift",
    url: "/ui/widgets",
    children: [
      {
        key: "widgets1",
        label: "Widgets",
        url: "/ui/widgets",
        parentKey: "widgets",
      },
    ],
  },
  // {
  //     key: 'widgets',
  //     label: 'Widgets',
  //     isTitle: false,
  //     icon: 'gift',
  //     url: '/ui/widgets',
  // },
];

export { MENU_ITEMS, TWO_COl_MENU_ITEMS, HORIZONTAL_MENU_ITEMS };
